import { createContext, useState, useContext, useMemo } from 'react';

import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import Slide from '@mui/material/Slide';
import useMediaQuery from '@mui/material/useMediaQuery';

import { ThemeButtonIcon } from '@shared/ui/buttons';
import { icons } from '@shared/static/icons';
import { useTranslation } from 'react-i18next';
import { Whisper, Tooltip } from 'rsuite';
import { theme } from '@utils/libs/theme';

import deleteImg from '@shared/assets/top-img-delete.svg';
import freezeImg from '@shared/assets/top-img-freeze.svg';
import cardsImg from '@shared/assets/top-img-cards.svg';

const DialogContext = createContext(null);
const DialogStateContext = createContext(null);

const images = {
  delete: { src: deleteImg, width: 360, height: 206 },
  freeze: { src: freezeImg, width: 360, height: 206 },
  cards: { src: cardsImg, width: 360, height: 108 },
};

const cn = (...classes) => classes.filter(Boolean).join(' ');

const INITIAL_STATE = {
  isOpen: false,
  title: '',
  subtitle: '',
  contentComponent: null,
  data: null,

  titleIcon: null, // warning, error
  titleAction: null,

  hideCloseButton: false,
  hint: '',

  bottomTransition: false,
  classNames: '', // top-img-style
  image: null, // delete, freeze, cards
  maxWidth: 'xs',
};

export function DialogProvider({ children }) {
  const [modal, setModal] = useState(INITIAL_STATE);

  const contextValue = useMemo(() => {
    const setField = (key) => (value) =>
      setModal((prev) => ({ ...prev, [key]: value }));

    return {
      openModal: (contentComponent, props = {}) =>
        setModal({
          ...INITIAL_STATE,
          ...props,
          isOpen: true,
          contentComponent,
        }),
      closeModal: () => setModal((prev) => ({ ...prev, isOpen: false })),
      setTitle: setField('title'),
      setSubtitle: setField('subtitle'),
      setTitleIcon: setField('titleIcon'),
      setTitleAction: setField('titleAction'),
      setHideCloseButton: setField('hideCloseButton'),
      setClassNames: setField('classNames'),
      setImage: setField('image'),
      setHint: setField('hint'),
    };
  }, []);

  return (
    <DialogContext.Provider value={contextValue}>
      <DialogStateContext.Provider value={modal}>
        {children}
      </DialogStateContext.Provider>
    </DialogContext.Provider>
  );
}

export function DialogViewport() {
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation();

  const modal = useContext(DialogStateContext);
  const actions = useDialogContext();

  const Content = modal.contentComponent;
  const image = images[modal.image];

  return (
    <Dialog
      open={modal.isOpen}
      onClose={actions.closeModal}
      maxWidth={modal.maxWidth}
      scroll="paper"
      className={cn(
        'customDialog',
        modal.classNames,
        modal.bottomTransition && 'bottom-transition',
      )}
      aria-labelledby="scroll-dialog-title"
      fullWidth
      fullScreen={isMobile}
      {...(modal.bottomTransition ? { slots: { transition: Slide } } : {})}
      slotProps={{
        ...(modal.bottomTransition ? { transition: { direction: 'up' } } : {}),
      }}
    >
      {image && <img className="top-img-style" alt="" {...image} />}

      <DialogTitle id="scroll-dialog-title" className="customDialog__heading">
        <div>
          <div
            className={cn(
              modal.subtitle && 'has-subtitle',
              modal.titleIcon && 'customDialog__heading-icon',
            )}
          >
            {modal.titleIcon && icons[modal.titleIcon]}

            <p
              className={cn(
                'customDialog__heading-title',
                modal.subtitle ? 'title-h3m' : 'title-h2m',
              )}
            >
              {modal.title && t(modal.title, { defaultValue: modal.title })}

              {modal.hint && (
                <Whisper
                  placement="auto"
                  trigger="hover"
                  speaker={<Tooltip>{modal.hint}</Tooltip>}
                >
                  <button type="button">{icons.hint}</button>
                </Whisper>
              )}
            </p>
          </div>

          {modal.subtitle && (
            <div
              className={cn(
                'd-flex body-14r secondary-text',
                modal.titleIcon && 'customDialog__subtitle',
              )}
            >
              {modal.subtitle}
            </div>
          )}
        </div>

        <div className="customDialog__heading-actions">
          {modal.titleAction && <div>{modal.titleAction}</div>}
          {!modal.hideCloseButton && (
            <ThemeButtonIcon icon="close" onClick={actions.closeModal} />
          )}
        </div>
      </DialogTitle>

      {Content && (
        <Content {...modal} {...actions} close={actions.closeModal} />
      )}
    </Dialog>
  );
}

export function useDialogContext() {
  const ctx = useContext(DialogContext);
  if (!ctx) {
    throw new Error('useDialogContext must be used within DialogProvider');
  }
  return ctx;
}
